import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, RefreshCw, PlusCircle, Compass, CheckCircle2, AlertTriangle, Trash2, Loader2, ChevronDown, ChevronUp, Target, ListChecks } from "lucide-react";
import GscPanel from "./GscPanel";
import GoogleAdsStatusPanel from "./GoogleAdsStatusPanel";
import SeoPlanPanel from "./SeoPlanPanel";
import { useAdminT } from "@/contexts/AdminLanguageContext";

/**
 * Prompt 16 — SLR AI SEO STUDIO
 * Simplified in-admin content engine. Three modes:
 *   1. Improve Existing Page — pick a guide and let the AI rewrite it.
 *   2. Create New Page — give a topic; AI produces EN/ES/RU draft.
 *   3. Discover Opportunities — AI suggests SEO gaps based on active fleet.
 *
 * All generations run as background jobs. UI polls the draft until it flips
 * from "pending" -> "draft" (or "failed"). Nothing is auto-published: the
 * editor approves each draft, which then lands in Guides (active=false).
 */

const MODES = [
  { key: "analyze", labelKey: "ai.mode.analyze", icon: RefreshCw },
  { key: "create", labelKey: "ai.mode.create", icon: PlusCircle },
  { key: "discover", labelKey: "ai.mode.discover", icon: Compass },
];

const LANGS = ["es", "en", "ru", "fr", "de", "it", "nl", "pl"];
const LANG_LABEL = Object.fromEntries(LANGS.map((lang) => [lang, lang.toUpperCase()]));

function StatusPill({ status }) {
  const { t } = useAdminT();
  const map = {
    pending: { bg: "bg-[#3a2c15]", fg: "text-[#C9A55A]", label: t("ai.status.pending") },
    draft: { bg: "bg-[#1e3a2a]", fg: "text-[#66d18a]", label: t("ai.status.draft") },
    approved: { bg: "bg-[#233657]", fg: "text-[#8ab6ff]", label: t("ai.status.approved") },
    failed: { bg: "bg-[#4a1e1e]", fg: "text-[#e57373]", label: t("ai.status.failed") },
  };
  const s = map[status] || { bg: "bg-[#2A2A2A]", fg: "text-[#8A8A8A]", label: status };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] ${s.bg} ${s.fg}`}>
      {status === "pending" && <Loader2 size={10} className="animate-spin" />} {s.label}
    </span>
  );
}

function LangPreview({ content, field, langs = LANGS }) {
  const [lang, setLang] = useState("en");
  const value = ((content || {})[field] || {})[lang] || "";
  return (
    <div className="border border-[#2A2A2A] mt-2">
      <div className="flex border-b border-[#2A2A2A]">
        {langs.map((l) => (
          <button
            key={l}
            onClick={() => setLang(l)}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] ${
              lang === l ? "bg-[#2A2A2A] text-[#C9A55A]" : "text-[#8A8A8A] hover:text-[#F5F1EB]"
            }`}
          >
            {LANG_LABEL[l]}
          </button>
        ))}
      </div>
      <div
        className="p-3 text-[13px] text-[#F5F1EB] max-h-64 overflow-auto whitespace-pre-wrap"
        dangerouslySetInnerHTML={{ __html: value || "<em>(empty)</em>" }}
      />
    </div>
  );
}

function DraftCard({ draft, onApprove, onDelete, onRefresh, onAudit, onAutoFix, onRegenerate, guides, vehicles }) {
  const { t } = useAdminT();
  const [open, setOpen] = useState(draft.status === "draft" || draft.status === "failed");
  const c = draft.content || {};
  const title = c.title?.en || draft.sourceTitleEn || draft.topic || "(untitled)";

  const vehicleName = (id) => (vehicles.find((v) => v.id === id) || {}).name || id;

  return (
    <div className="border border-[#2A2A2A]" data-testid={`ai-draft-${draft.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-[#2A2A2A]">
        <div className="flex items-center gap-3">
          <StatusPill status={draft.status} />
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">
            {draft.mode}
          </span>
          <span className="text-[#F5F1EB] text-sm">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {draft.status === "pending" && (
            <button
              onClick={onRefresh}
              className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A] hover:text-[#C9A55A]"
              data-testid={`ai-draft-refresh-${draft.id}`}
            >
              Refresh
            </button>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            className="text-[#8A8A8A] hover:text-[#C9A55A]"
            data-testid={`ai-draft-toggle-${draft.id}`}
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button
            onClick={() => onDelete(draft.id)}
            className="text-[#B8342A] hover:opacity-80"
            data-testid={`ai-draft-delete-${draft.id}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {open && (
        <div className="p-3">
          {draft.status === "failed" && (
            <div className="border border-[#4a1e1e] bg-[#1a0d0d] text-[#e57373] p-3 text-xs mb-3">
              <AlertTriangle size={12} className="inline mr-1" />
              {draft.error || t("ai.status.failed")}
            </div>
          )}

          {draft.mode === "discover" && draft.status === "draft" && (
            <div className="space-y-2">
              {(draft.opportunities || []).map((o, i) => (
                <div key={i} className="border border-[#2A2A2A] p-3" data-testid={`ai-opportunity-${i}`}>
                  <div className="flex items-baseline justify-between">
                    <div className="text-[#F5F1EB] font-medium">{o.topic}</div>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#C9A55A]">
                      P{o.priority} · {o.estimatedIntent}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#8A8A8A] mt-1">
                    kw: <span className="text-[#F5F1EB]">{o.focusKeyword}</span> · slug: <span className="font-mono">{o.suggestedSlugEn}</span>
                  </div>
                  <div className="text-xs text-[#F5F1EB] mt-2">{o.rationale}</div>
                  {(o.matchingVehicleNames || []).length > 0 && (
                    <div className="text-[10px] text-[#8A8A8A] mt-1">
                      {t("ai.field.rec_fleet")}: {o.matchingVehicleNames.join(", ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {draft.status === "draft" && draft.mode !== "discover" && (
            <>
              {(draft.factualQA || []).length > 0 && (
                <div className="border border-[#4a1e1e] bg-[#1a0d0d] text-[#e57373] p-3 text-xs mb-3">
                  <AlertTriangle size={12} className="inline mr-1" />
                  {draft.factualQA.length === 1 ? t("ai.qa.flagged_one") : t("ai.qa.flagged_many", { n: draft.factualQA.length })}
                  <ul className="mt-1 ml-4 list-disc">
                    {draft.factualQA.map((i, idx) => <li key={idx}>{i}</li>)}
                  </ul>
                </div>
              )}
              {draft.originality && (
                <div className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.18em] mb-2">
                  {t("ai.originality.label", { score: (draft.originality.score * 100).toFixed(1), overlap: (draft.originality.overlapRatio * 100).toFixed(1) })}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-overline text-[#C9A55A]">{t("ai.field.title")}</div>
                  <LangPreview content={c} field="title" />
                </div>
                <div>
                  <div className="text-overline text-[#C9A55A]">{t("ai.field.seo_title")}</div>
                  <LangPreview content={c} field="seoTitle" />
                </div>
                <div>
                  <div className="text-overline text-[#C9A55A]">{t("ai.field.meta")}</div>
                  <LangPreview content={c} field="metaDescription" />
                </div>
                <div>
                  <div className="text-overline text-[#C9A55A]">{t("ai.field.intro")}</div>
                  <LangPreview content={c} field="intro" />
                </div>
                <div className="md:col-span-2">
                  <div className="text-overline text-[#C9A55A]">{t("ai.field.body")}</div>
                  <LangPreview content={c} field="body" />
                </div>
              </div>

              {(c.recommendedVehicleIds || []).length > 0 && (
                <div className="mt-3 text-[11px] text-[#8A8A8A]">
                  {t("ai.field.rec_fleet")}: <span className="text-[#F5F1EB]">
                    {c.recommendedVehicleIds.map(vehicleName).join(" · ")}
                  </span>
                </div>
              )}

              {(draft.improvements || []).length > 0 && (
                <div className="mt-3 text-xs text-[#8A8A8A]">
                  <div className="text-overline text-[#C9A55A] mb-1">{t("ai.field.improvements")}</div>
                  <ul className="list-disc ml-4">
                    {draft.improvements.map((i, idx) => <li key={idx}>{i}</li>)}
                  </ul>
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {/* Prompt Final Global — 3 distinct QA/generation actions */}
                <button
                  onClick={() => onAudit(draft.id)}
                  className="px-3 h-9 border border-[#4a6bff] text-[#8ab6ff] text-[10px] uppercase tracking-[0.18em] hover:bg-[#8ab6ff] hover:text-[#0C0C0C]"
                  data-testid={`ai-draft-reaudit-${draft.id}`}
                  title="Vuelve a analizar QA contra la flota actual y Business Facts."
                >
                  Reanalizar QA
                </button>
                {(draft.factualQA || []).length > 0 && (
                  <button
                    onClick={() => onAutoFix(draft.id)}
                    className="px-3 h-9 border border-[#66d18a] text-[#66d18a] text-[10px] uppercase tracking-[0.18em] hover:bg-[#66d18a] hover:text-[#0C0C0C]"
                    data-testid={`ai-draft-autofix-${draft.id}`}
                    title="Envía las incidencias de QA a la IA para corregirlas (máx. 3 ciclos)."
                  >
                    Corregir con IA
                  </button>
                )}
                <button
                  onClick={() => onRegenerate(draft.id)}
                  className="px-3 h-9 border border-[#C9A55A] text-[#C9A55A] text-[10px] uppercase tracking-[0.18em] hover:bg-[#C9A55A] hover:text-[#0C0C0C]"
                  data-testid={`ai-draft-regenerate-${draft.id}`}
                  title="Rehace TODO el contenido conservando el blueprint y el intent."
                >
                  Regenerar borrador completo
                </button>
                <div className="w-full h-px bg-[#2A2A2A] my-1" />
                {(() => {
                  const qaBlocked = (draft.factualQA || []).length > 0;
                  const btnCls = qaBlocked
                    ? "px-4 h-10 border border-[#4a1e1e] bg-[#1a0d0d] text-[#e57373] text-[11px] uppercase tracking-[0.18em] cursor-not-allowed opacity-70"
                    : "btn-gold";
                  const btnCls2 = qaBlocked
                    ? "px-4 h-10 border border-[#4a1e1e] bg-[#1a0d0d] text-[#e57373] text-[11px] uppercase tracking-[0.18em] cursor-not-allowed opacity-70"
                    : "px-4 h-10 border border-[#C9A55A]/40 text-[#C9A55A] text-[11px] uppercase tracking-[0.18em] hover:bg-[#C9A55A] hover:text-[#0C0C0C]";
                  return (
                    <>
                      <button
                        onClick={() => onApprove(draft.id, false)}
                        className={btnCls}
                        disabled={qaBlocked}
                        title={qaBlocked ? t("ai.errors.qa_blocked") : ""}
                        data-testid={`ai-draft-approve-${draft.id}`}
                      >
                        <CheckCircle2 size={14} className="inline mr-1" />
                        {t("ai.actions.approve_unpublished")}
                      </button>
                      <button
                        onClick={() => onApprove(draft.id, true)}
                        className={btnCls2}
                        disabled={qaBlocked}
                        title={qaBlocked ? t("ai.errors.qa_blocked") : ""}
                        data-testid={`ai-draft-approve-live-${draft.id}`}
                      >
                        {t("ai.actions.approve_publish")}
                      </button>
                      {qaBlocked && (
                        <button
                          onClick={() => onApprove(draft.id, false, true)}
                          className="px-4 h-10 border border-[#B8342A] text-[#B8342A] text-[11px] uppercase tracking-[0.18em] hover:bg-[#B8342A] hover:text-[#F5F1EB]"
                          data-testid={`ai-draft-force-${draft.id}`}
                          title={t("ai.actions.confirm_qa_override")}
                        >
                          {t("ai.actions.force_override")}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AiSeoAdmin() {
  const { t } = useAdminT();
  const [mode, setMode] = useState("create");
  const [primaryTab, setPrimaryTab] = useState("studio");  // "studio" | "gsc"
  const [guides, setGuides] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [analyzeGuideId, setAnalyzeGuideId] = useState("");
  const [focusKeyword, setFocusKeyword] = useState("");
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [category, setCategory] = useState("guide");
  const [useWebSearch, setUseWebSearch] = useState(true);
  const [seedTopics, setSeedTopics] = useState("");

  const loadLookups = async () => {
    try {
      const { data } = await api.get("/admin/ai-seo/lookups");
      setGuides(data.guides || []);
      setVehicles(data.vehicles || []);
    } catch { /* ignore */ }
  };
  const loadDrafts = async () => {
    try {
      const { data } = await api.get("/admin/ai-seo/drafts");
      setDrafts(data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadLookups(); loadDrafts(); }, []);

  // auto-poll while any draft is pending
  const anyPending = useMemo(() => drafts.some((d) => d.status === "pending"), [drafts]);
  useEffect(() => {
    if (!anyPending) return;
    const t = setInterval(loadDrafts, 5000);
    return () => clearInterval(t);
  }, [anyPending]);

  const submit = async () => {
    if (anyPending) {
      alert("A generation is already in progress. Please wait for it to finish before starting a new one (this plan allows one concurrent AI job).");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "analyze") {
        if (!analyzeGuideId) { alert("Pick a guide first"); return; }
        await api.post("/admin/ai-seo/analyze", { guideId: analyzeGuideId, focusKeyword, notes });
      } else if (mode === "create") {
        if (!topic.trim()) { alert("Topic is required"); return; }
        await api.post("/admin/ai-seo/create", { topic, focusKeyword, category, notes, blueprintId });
      } else if (mode === "discover") {
        const seeds = seedTopics.split(",").map((s) => s.trim()).filter(Boolean);
        await api.post("/admin/ai-seo/discover", { useWebSearch, seedTopics: seeds });
      }
      setTopic(""); setFocusKeyword(""); setNotes(""); setSeedTopics(""); setBlueprintId(null);
      await loadDrafts();
    } catch (e) {
      alert(e.response?.data?.detail || "Generation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const approve = async (id, active, forceOverride = false) => {
    if (forceOverride) {
      const ok = window.confirm(t("ai.actions.confirm_qa_override"));
      if (!ok) return;
      try {
        // Prompt Final closure — force override CAN ONLY save as internal draft
        // (active=false). Publishing with a CRITICAL Factual QA is BLOCKED
        // in both frontend and backend.
        const { data } = await api.post(`/admin/ai-seo/drafts/${id}/approve`, { active: false, force: true });
        alert(`Guide ${data.mode}: ${data.guideId}${data.slugEn ? " (" + data.slugEn + ")" : ""}. Guardado internamente (no publicado). Revisa las incidencias antes de activar.`);
        await loadDrafts();
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail?.code === "CRITICAL_QA_PUBLISH_BLOCKED") {
          alert(`❌ ${detail.message}\n\n• ${(detail.issues||[]).join("\n• ")}`);
        } else {
          alert(detail?.message || detail || "Approve failed");
        }
      }
      return;
    }
    try {
      const { data } = await api.post(`/admin/ai-seo/drafts/${id}/approve`, { active });
      alert(`Guide ${data.mode}: ${data.guideId}${data.slugEn ? " (" + data.slugEn + ")" : ""}. Open Guides admin to review.`);
      await loadDrafts();
    } catch (e) {
      // Prompt 18 — 409 = Factual QA blocked
      if (e.response?.status === 409) {
        const detail = e.response.data?.detail;
        const issues = Array.isArray(detail?.issues) ? detail.issues.join("\n• ") : "";
        alert(`${t("ai.errors.qa_blocked")}\n\n• ${issues}\n\n${t("ai.errors.qa_use_override")}`);
        return;
      }
      alert(e.response?.data?.detail || "Approve failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    await api.delete(`/admin/ai-seo/drafts/${id}`);
    await loadDrafts();
  };

  const audit = async (id) => {
    try {
      const { data } = await api.post(`/admin/ai-seo/drafts/${id}/audit`);
      alert(`Reanalizado. Incidencias detectadas: ${data.count || 0}`);
      await loadDrafts();
    } catch (e) {
      alert(e.response?.data?.detail || "Audit failed");
    }
  };

  const autoFix = async (id) => {
    if (!window.confirm("Corregir con IA aplicará hasta 3 ciclos de arreglo automático manteniendo el blueprint, el intent y el mercado. ¿Continuar?")) return;
    try {
      await api.post(`/admin/ai-seo/drafts/${id}/auto-fix`, { maxCycles: 3 });
      alert("Auto-Fix encolado. El borrador se actualizará en unos minutos.");
      await loadDrafts();
    } catch (e) {
      alert(e.response?.data?.detail || "Auto-fix failed");
    }
  };

  const regenerate = async (id) => {
    if (!window.confirm("Regenerar borrador completo rehará TODO el contenido pero mantendrá el blueprint y el intent. ¿Continuar?")) return;
    try {
      await api.post(`/admin/ai-seo/drafts/${id}/regenerate`, {});
      alert("Regeneración encolada. El borrador se rehará en unos minutos.");
      await loadDrafts();
    } catch (e) {
      alert(e.response?.data?.detail || "Regenerate failed");
    }
  };

  // Handoffs from GSC panel -> existing Studio modes
  const handleSendToImprove = (opp) => {
    setPrimaryTab("studio");
    setMode("analyze");
    if (opp.guideId) setAnalyzeGuideId(opp.guideId);
    else if (opp.guide?.id) setAnalyzeGuideId(opp.guide.id);
    setFocusKeyword(opp.query || "");
    const gscBits = [
      opp.position != null ? `Current position: ${opp.position}` : null,
      opp.impressions != null ? `Impressions: ${opp.impressions}` : null,
      opp.clicks != null ? `Clicks: ${opp.clicks}` : null,
      opp.ctr != null ? `CTR: ${opp.ctr}%` : null,
      opp.category ? `GSC category: ${opp.category}` : null,
    ].filter(Boolean).join(" · ");
    setNotes(gscBits ? `From Search Console — ${gscBits}. Please improve title, meta description, and body to increase CTR while staying grounded on the active fleet.` : "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [blueprintId, setBlueprintId] = useState(null);
  const handleSendToCreate = (opp) => {
    setPrimaryTab("studio");
    setMode("create");
    setTopic(opp.query || opp.topic || "");
    setFocusKeyword(opp.query || opp.focusKeyword || "");
    setBlueprintId(opp.blueprintId || null);
    const gscBits = [
      opp.position != null ? `current position ${opp.position}` : null,
      opp.impressions != null ? `${opp.impressions} impressions` : null,
      opp.category ? `category ${opp.category}` : null,
    ].filter(Boolean).join(", ");
    const bpNote = opp.notes ? opp.notes : (gscBits ? `From Search Console opportunity: ${gscBits}. Create a dedicated page grounded on the active fleet. Do NOT duplicate an existing guide.` : "");
    setNotes(bpNote);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="admin-ai-seo-page">
      <div className="flex items-baseline justify-between mb-2">
        <h1 className="font-display text-[#F5F1EB] text-3xl flex items-center gap-2">
          <Sparkles size={22} className="text-[#C9A55A]" /> {t("ai.title")}
        </h1>
        <span className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.18em]">
          {t("ai.model_label")}
        </span>
      </div>
      <p className="text-sm text-[#8A8A8A] mb-6 max-w-3xl">
        {t("ai.subtitle")}
      </p>

      {/* Primary tabs: Studio vs GSC */}
      <div className="flex gap-1 border-b border-[#2A2A2A] mb-6" data-testid="ai-primary-tabs">
        <button
          onClick={() => setPrimaryTab("studio")}
          className={`px-4 h-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] border-b-2 ${primaryTab === "studio" ? "border-[#C9A55A] text-[#C9A55A]" : "border-transparent text-[#8A8A8A] hover:text-[#F5F1EB]"}`}
          data-testid="ai-primary-studio"
        >
          <Sparkles size={14} /> {t("ai.tab.studio")}
        </button>
        <button
          onClick={() => setPrimaryTab("gsc")}
          className={`px-4 h-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] border-b-2 ${primaryTab === "gsc" ? "border-[#C9A55A] text-[#C9A55A]" : "border-transparent text-[#8A8A8A] hover:text-[#F5F1EB]"}`}
          data-testid="ai-primary-gsc"
        >
          <Target size={14} /> {t("ai.tab.gsc")}
        </button>
        <button
          onClick={() => setPrimaryTab("plan")}
          className={`px-4 h-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] border-b-2 ${primaryTab === "plan" ? "border-[#C9A55A] text-[#C9A55A]" : "border-transparent text-[#8A8A8A] hover:text-[#F5F1EB]"}`}
          data-testid="ai-primary-plan"
        >
          <ListChecks size={14} /> Plan SEO
        </button>
      </div>

      <GoogleAdsStatusPanel />
      {primaryTab === "gsc" ? (
        <GscPanel onSendToImprove={handleSendToImprove} onSendToCreate={handleSendToCreate} />
      ) : primaryTab === "plan" ? (
        <SeoPlanPanel onSendToImprove={handleSendToImprove} onSendToCreate={handleSendToCreate} />
      ) : (
      <>
      {/* Mode tabs */}
      <div className="flex gap-1 border border-[#2A2A2A] p-1 w-fit mb-6" data-testid="ai-mode-tabs">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={`px-3 h-9 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] ${
                active ? "bg-[#2A2A2A] text-[#C9A55A]" : "text-[#F5F1EB] hover:text-[#C9A55A]"
              }`}
              data-testid={`ai-mode-${m.key}`}
            >
              <Icon size={14} /> {t(m.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Form */}
      <div className="border border-[#2A2A2A] p-4 mb-8" data-testid="ai-form">
        {mode === "analyze" && (
          <>
            <div className="text-overline text-[#C9A55A] mb-3">{t("ai.form.analyze_heading")}</div>
            <label className="text-[11px] text-[#8A8A8A] uppercase tracking-[0.16em]">{t("ai.form.guide")}</label>
            <select
              value={analyzeGuideId}
              onChange={(e) => setAnalyzeGuideId(e.target.value)}
              className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm mt-1 mb-3"
              data-testid="ai-analyze-guide-select"
            >
              <option value="">{t("ai.form.guide_placeholder")}</option>
              {guides.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.titleEn || g.slugEn} {g.active ? "" : "· inactive"}
                </option>
              ))}
            </select>
          </>
        )}
        {mode === "create" && (
          <>
            <div className="text-overline text-[#C9A55A] mb-3">{t("ai.form.create_heading")}</div>
            <label className="text-[11px] text-[#8A8A8A] uppercase tracking-[0.16em]">{t("ai.form.topic")}</label>
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t("ai.form.topic_placeholder")}
              className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm mt-1 mb-3"
              data-testid="ai-create-topic"
            />
            <label className="text-[11px] text-[#8A8A8A] uppercase tracking-[0.16em]">{t("ai.form.category")}</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm mt-1 mb-3"
              data-testid="ai-create-category"
            >
              <option value="guide">{t("ai.form.category.guide")}</option>
              <option value="route">{t("ai.form.category.route")}</option>
              <option value="event">{t("ai.form.category.event")}</option>
            </select>
          </>
        )}
        {mode === "discover" && (
          <>
            <div className="text-overline text-[#C9A55A] mb-3">{t("ai.form.discover_heading")}</div>
            <label className="text-[11px] text-[#8A8A8A] uppercase tracking-[0.16em]">{t("ai.form.seeds")}</label>
            <input
              value={seedTopics}
              onChange={(e) => setSeedTopics(e.target.value)}
              placeholder={t("ai.form.seeds_placeholder")}
              className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm mt-1 mb-3"
              data-testid="ai-discover-seeds"
            />
            <label className="flex items-center gap-2 text-sm text-[#F5F1EB] mb-3">
              <input
                type="checkbox"
                checked={useWebSearch}
                onChange={(e) => setUseWebSearch(e.target.checked)}
                data-testid="ai-discover-web"
              />
              {t("ai.form.web_search")}
            </label>
          </>
        )}

        {mode !== "discover" && (
          <>
            <label className="text-[11px] text-[#8A8A8A] uppercase tracking-[0.16em]">{t("ai.form.focus_keyword")}</label>
            <input
              value={focusKeyword}
              onChange={(e) => setFocusKeyword(e.target.value)}
              placeholder={t("ai.form.focus_placeholder")}
              className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm mt-1 mb-3"
              data-testid="ai-focus-keyword"
            />
            <label className="text-[11px] text-[#8A8A8A] uppercase tracking-[0.16em]">{t("ai.form.notes")}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={t("ai.form.notes_placeholder")}
              className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 py-2 text-sm mt-1 mb-3"
              data-testid="ai-notes"
            />
          </>
        )}

        <button
          onClick={submit}
          disabled={submitting || anyPending}
          className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="ai-submit"
        >
          {submitting || anyPending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {submitting ? t("ai.form.submit_queuing") : anyPending ? t("ai.form.submit_waiting") : t("ai.form.submit")}
        </button>
        <div className="text-[10px] text-[#8A8A8A] mt-2">
          {t("ai.form.hint")}
        </div>
      </div>

      {/* Drafts list */}
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-overline text-[#C9A55A]">{t("ai.drafts.heading")}</h2>
        <button
          onClick={loadDrafts}
          className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A] hover:text-[#C9A55A]"
          data-testid="ai-refresh-drafts"
        >
          {t("common.refresh")}
        </button>
      </div>
      {loading ? (
        <div className="text-[#8A8A8A]">{t("common.loading")}</div>
      ) : drafts.length === 0 ? (
        <div className="border border-dashed border-[#2A2A2A] p-8 text-center text-[#8A8A8A]" data-testid="ai-drafts-empty">
          {t("ai.drafts.empty")}
        </div>
      ) : (
        <div className="space-y-3">
          {drafts.map((d) => (
            <DraftCard
              key={d.id}
              draft={d}
              guides={guides}
              vehicles={vehicles}
              onApprove={approve}
              onDelete={remove}
              onRefresh={loadDrafts}
              onAudit={audit}
              onAutoFix={autoFix}
              onRegenerate={regenerate}
            />
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
